<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  countVariables,
  buildFormData,
  validate,
} from './helpers/templateForm';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const store = useStore();
const { t } = useI18n();
const dialogRef = ref(null);

const LANGUAGES = ['pt_BR', 'en', 'en_US', 'es'];
const CATEGORIES = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];
const HEADER_TYPES = ['NONE', 'TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'];
const BUTTON_TYPES = ['QUICK_REPLY', 'URL', 'PHONE_NUMBER'];

const blank = () => ({
  name: '',
  language: 'pt_BR',
  category: 'MARKETING',
  headerType: 'NONE',
  headerText: '',
  headerFile: null,
  bodyText: '',
  bodyExamples: [],
  footerText: '',
  buttons: [],
});

const form = reactive(blank());
const errorMessage = ref(null);

const variableCount = computed(() => countVariables(form.bodyText));
const isMediaHeader = computed(() =>
  ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.headerType)
);
const isCreating = computed(
  () => store.getters['whatsappTemplates/getUIFlags'].isCreating
);

// Um campo de exemplo por variável, preservando o que já foi digitado.
watch(variableCount, count => {
  form.bodyExamples = Array.from(
    { length: count },
    (_, index) => form.bodyExamples[index] || ''
  );
});

const addButton = type => {
  const blanks = {
    QUICK_REPLY: { type: 'QUICK_REPLY', text: '' },
    URL: { type: 'URL', text: '', url: '', example: '' },
    PHONE_NUMBER: { type: 'PHONE_NUMBER', text: '', phone_number: '' },
  };
  form.buttons.push({ ...blanks[type] });
};

const onFile = event => {
  const [file] = event.target.files || [];
  form.headerFile = file || null;
};

const submit = async () => {
  errorMessage.value = validate(form);
  if (errorMessage.value) return;

  try {
    await store.dispatch('whatsappTemplates/create', {
      inboxId: props.inbox.id,
      formData: buildFormData(form),
    });
    useAlert(t('CANNED_MGMT.WHATSAPP_TEMPLATES.FORM.SUCCESS'));
    dialogRef.value?.close();
    Object.assign(form, blank());
  } catch (error) {
    errorMessage.value = store.getters['whatsappTemplates/getError'];
  }
};

const open = () => {
  Object.assign(form, blank());
  errorMessage.value = null;
  dialogRef.value?.open();
};
defineExpose({ open });

const inputClass =
  'w-full h-10 px-3 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12';
// Selects herdam o estilo global (_base.scss), que desenha a seta do dropdown
// como background contando com o padding-right dele — sobrescrever o padding
// escondia a seta.
const selectClass = 'w-full mb-0';
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    position="top"
    :title="$t('CANNED_MGMT.WHATSAPP_TEMPLATES.FORM.TITLE')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    overflow-y-auto
  >
    <div class="flex w-full gap-6">
      <div class="flex flex-col flex-1 gap-3">
        <p class="mb-0 text-xs text-n-amber-11">
          {{ $t('CANNED_MGMT.WHATSAPP_TEMPLATES.FORM.IRREVERSIBLE') }}
        </p>
        <p v-if="errorMessage" class="mb-0 text-sm text-n-ruby-11">
          {{ errorMessage }}
        </p>

        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-11">
            {{ $t('CANNED_MGMT.WHATSAPP_TEMPLATES.FORM.NAME') }}
          </span>
          <input v-model="form.name" type="text" :class="inputClass" />
          <span class="text-xs text-n-slate-10">
            {{ $t('CANNED_MGMT.WHATSAPP_TEMPLATES.FORM.NAME_HINT') }}
          </span>
        </label>

        <div class="flex gap-2">
          <label class="flex flex-col flex-1 gap-1">
            <span class="text-sm text-n-slate-11">
              {{ $t('CANNED_MGMT.WHATSAPP_TEMPLATES.FORM.LANGUAGE') }}
            </span>
            <select v-model="form.language" :class="selectClass">
              <option v-for="code in LANGUAGES" :key="code" :value="code">
                {{ code }}
              </option>
            </select>
          </label>
          <label class="flex flex-col flex-1 gap-1">
            <span class="text-sm text-n-slate-11">
              {{ $t('CANNED_MGMT.WHATSAPP_TEMPLATES.FORM.CATEGORY') }}
            </span>
            <select v-model="form.category" :class="selectClass">
              <option
                v-for="category in CATEGORIES"
                :key="category"
                :value="category"
              >
                {{
                  $t(`CANNED_MGMT.WHATSAPP_TEMPLATES.FORM.CATEGORY_${category}`)
                }}
              </option>
            </select>
          </label>
        </div>

        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-11">
            {{ $t('CANNED_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER') }}
          </span>
          <select v-model="form.headerType" :class="selectClass">
            <option v-for="type in HEADER_TYPES" :key="type" :value="type">
              {{ $t(`CANNED_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_${type}`) }}
            </option>
          </select>
        </label>

        <input
          v-if="form.headerType === 'TEXT'"
          v-model="form.headerText"
          type="text"
          :class="inputClass"
          :placeholder="$t('CANNED_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_TEXT')"
        />
        <label v-if="isMediaHeader" class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-10">
            {{ $t('CANNED_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_SAMPLE') }}
          </span>
          <input type="file" @change="onFile" />
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-11">
            {{ $t('CANNED_MGMT.WHATSAPP_TEMPLATES.FORM.BODY') }}
          </span>
          <textarea v-model="form.bodyText" rows="4" :class="inputClass" />
          <span class="text-xs text-n-slate-10">
            {{ $t('CANNED_MGMT.WHATSAPP_TEMPLATES.FORM.BODY_HINT') }}
          </span>
        </label>

        <input
          v-for="(example, index) in form.bodyExamples"
          :key="index"
          v-model="form.bodyExamples[index]"
          type="text"
          :class="inputClass"
          :placeholder="
            $t('CANNED_MGMT.WHATSAPP_TEMPLATES.FORM.EXAMPLE_FOR', {
              variable: index + 1,
            })
          "
        />

        <input
          v-model="form.footerText"
          type="text"
          :class="inputClass"
          :placeholder="$t('CANNED_MGMT.WHATSAPP_TEMPLATES.FORM.FOOTER')"
        />

        <div class="flex flex-col gap-2">
          <span class="text-sm text-n-slate-11">
            {{ $t('CANNED_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS') }}
          </span>
          <div
            v-for="(button, index) in form.buttons"
            :key="index"
            class="flex flex-col gap-2 p-2 border rounded-lg border-n-weak"
          >
            <div class="flex items-center justify-between">
              <span class="text-xs font-medium text-n-slate-11">
                {{ button.type }}
              </span>
              <button
                type="button"
                class="text-xs bg-transparent border-none cursor-pointer text-n-ruby-11"
                @click="form.buttons.splice(index, 1)"
              >
                {{ $t('CANNED_MGMT.WHATSAPP_TEMPLATES.FORM.REMOVE') }}
              </button>
            </div>
            <input
              v-model="button.text"
              type="text"
              :class="inputClass"
              :placeholder="
                $t('CANNED_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTON_TEXT')
              "
            />
            <input
              v-if="button.type === 'URL'"
              v-model="button.url"
              type="text"
              :class="inputClass"
              :placeholder="
                $t('CANNED_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTON_URL')
              "
            />
            <input
              v-if="button.type === 'URL' && countVariables(button.url)"
              v-model="button.example"
              type="text"
              :class="inputClass"
              :placeholder="
                $t('CANNED_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTON_URL_EXAMPLE')
              "
            />
            <input
              v-if="button.type === 'PHONE_NUMBER'"
              v-model="button.phone_number"
              type="text"
              :class="inputClass"
              :placeholder="
                $t('CANNED_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTON_PHONE')
              "
            />
          </div>
          <div class="flex flex-wrap gap-2">
            <Button
              v-for="type in BUTTON_TYPES"
              :key="type"
              faded
              slate
              sm
              :label="$t(`CANNED_MGMT.WHATSAPP_TEMPLATES.FORM.ADD_${type}`)"
              @click="addButton(type)"
            />
          </div>
        </div>

        <div class="flex justify-end gap-2 pt-2">
          <Button
            faded
            slate
            sm
            :label="$t('CANNED_MGMT.WHATSAPP_TEMPLATES.FORM.CANCEL')"
            @click="dialogRef?.close()"
          />
          <Button
            solid
            blue
            sm
            :is-loading="isCreating"
            :label="$t('CANNED_MGMT.WHATSAPP_TEMPLATES.FORM.SUBMIT')"
            @click="submit"
          />
        </div>
      </div>

      <!-- Preview: balão do WhatsApp, atualizado ao digitar -->
      <div class="w-64 shrink-0">
        <p class="mb-2 text-xs text-n-slate-11">
          {{ $t('CANNED_MGMT.WHATSAPP_TEMPLATES.FORM.PREVIEW') }}
        </p>
        <div class="p-3 rounded-xl bg-n-teal-3">
          <p
            v-if="form.headerType === 'TEXT' && form.headerText"
            class="mb-1 text-sm font-semibold text-n-slate-12"
          >
            {{ form.headerText }}
          </p>
          <div
            v-else-if="isMediaHeader"
            class="flex items-center justify-center h-24 mb-1 text-xs rounded-lg bg-n-alpha-2 text-n-slate-11"
          >
            {{ form.headerType }}
          </div>
          <p class="mb-1 text-sm whitespace-pre-wrap text-n-slate-12">
            {{ form.bodyText }}
          </p>
          <p v-if="form.footerText" class="mb-0 text-xs text-n-slate-11">
            {{ form.footerText }}
          </p>
        </div>
        <div
          v-for="(button, index) in form.buttons"
          :key="`preview-${index}`"
          class="py-2 mt-1 text-sm text-center rounded-lg text-n-blue-11 bg-n-alpha-2"
        >
          {{ button.text || '...' }}
        </div>
      </div>
    </div>
  </Dialog>
</template>
